/*************************************************************************
 * ADOBE SYSTEMS INCORPORATED
 *  Copyright 2017 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying it.
 * If you have received this file from a source other than Adobe, then your
 * use, modification, or distribution of it requires the prior written
 * permission of Adobe.
 **************************************************************************/
/**
 * @file
 */

import { observable, action } from 'mobx';
// Get providers from the global environment rather than from an import
// That way we don't have a compile-time dependency and we avoid bi-directional
// dependencies between dc-core and dc-mobx
const core = window.adobe_dc_sdk.appLauncher.coreFunction();
const { providers, providerUtil } = core;

/**
 * @classdesc
 * A service provider for asset-related DCAPI operations.
 * @class
 * @see {@link https://createpdf.acrobat.com/static/documentation/dcapi/API/dcapi-static.html|DCAPI}
 */
class AssetAPI {
  /**
   * Allow for recent changes to be observable.
   * Note that not all changes may be observed this way, please check with the source.
   * e.g. {parent_uri: "...", asset_uri: "..."}
   */
  @observable recentChange;

  /**
   * Set a recent change for others to observe.
   */
  @action
  setRecentChange(change) {
    if (!change) {
      throw new TypeError('change must be an object');
    }
    this.recentChange = change;
  }

  /**
   * @description
   * Standard provider ready() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when asset provider has been instantiated
   * @public
   */
  ready() {
    return providers.asset2().then(asset2 => {
      this.asset2 = asset2;
      return asset2.ready().then(() => {
        this.dcapi = asset2.dcapi;
        asset2.useChange(this.setRecentChange.bind(this));
        return this;
      });
    });
  }

  /**
   * @description
   * Converts asset from its asset_id (URN), if it exists, to a contemporary (i.e. an asset uri with a fresh expiry)
   * asset URI for the specified asset id (URN).
   * @method
   * @param {Asset} asset - Input asset
   * @returns An asset URI (specified asset_uri).  If no asset_uri is present, the a uri created with the asset_id.
   * @private
   */
  uriFromAsset(asset) {
    return this.asset2.uriFromAsset(asset);
  }

  uriFromAssetPromise(asset) {
    return this.asset2.uriFromAssetPromise(asset);
  }

  /**
   * @description
   * Upload a file and receive a DCAPI asset_uri for it.
   * @method
   * @param {Asset} asset - Input asset which is Instance of Asset
   * @param {object} options - Parameters to append to the formData.
   * @returns {Promise} - A promise that resolves to the given asset JSON object now set with a asset_uri
   * @public
   */
  uploadFile(givenAsset, options) {
    return this.asset2.uploadFile(givenAsset, options);
  }

  /**
   * @description
   * Update file's content for a given asset.
   * @method
   * @param {Asset} asset - Input asset which is Instance of Asset, with { asset_id, inputFile }
   * @param options - Parameters for DCAPI that may include additional headers
   * @returns {Promise} - A promise that resolves to the metadata of the updated asset
   * @public
   */
  updateFile(assetToUpdate, options = {}) {
    return this.asset2.updateFile(assetToUpdate, options);
  }

  /**
   * @description
   * Get a URI to use for downloading actual file content-bytes.
   * @method
   * @param {Asset} asset - Input asset for getting its download URI
   * @param options - Parameters for DCAPI to get the download URI for given asset
   * @returns {Promise} - A promise that resolves to result containing the download URI
   * @public
   */
  getDownloadURI(assetToDownload, downloadOptions) {
    return this.asset2.getDownloadURI(assetToDownload, downloadOptions);
  }

  /**
   * @description
   * Get the metadata of the given asset.
   * @method
   * @param {Asset} asset - Input asset whose metadata is to be fetched
   * @returns {Promise} - A promise that resolves to JSON object with meta-data from DCAPI
   * @public
   */
  getAssetMetadata(asset) {
    return this.asset2.getAssetMetadata(asset);
  }

  /**
   * @description
   * Update the metadata of the given asset.
   * @method
   * @param {Asset} asset - Input asset whose metadata is to be updated
   * @returns {Promise} - A promise that resolves to JSON object with meta-data from DCAPI
   * @public
   */
  putAssetMetadata(asset, options) {
    return this.asset2.putAssetMetadata(asset, options);
  }

  /**
   * @description
   * Delete the given asset.
   * @method
   * @param {Asset} asset - asset with asset_uri to be deleted
   * @returns {Promise} - A promise that resolves to the uri of the deleted asset
   * @public
   */
  deleteAsset(asset) {
    return this.asset2.deleteAsset(asset);
  }

  /**
   * @description
   * Get a URI to the root folder.
   * @method
   * @param {object} options - options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to URI to the root folder
   * @public
   */
  getFolderRoot(options) {
    return this.asset2.getFolderRoot(options);
  }

  /**
   * @description
   * Lists the folder contents of the given folder.
   * @method
   * @param {string} folderUri - The URI for the folder contents to be listed
   * @param {object} options - the options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to JSON object with the folder structure from DCAPI
   * @public
   */
  getFolderList(folderUri, options) {
    return this.asset2.getFolderList(folderUri, options);
  }

  /**
   * @description
   * Gets the folder's breadcrumbs (path) from the root folder.
   * @method
   * @param {string} folderUri - The URI of the folder
   * @param {object} options - The options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A Promise that resolves to a JSON object with the folder's breadcrumbs from DCAPI
   * @public
   */
  getFolderBreadcrumbs(folderUri, options) {
    return this.asset2.getFolderBreadcrumbs(folderUri, options);
  }

  /**
   * @description
   * Get folder metadata given a folder_uri.
   * @method
   * @param {string} folderUri - The URI of the folder
   * @param {object} options - the options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to folder metadata
   * @public
   */
  getFolderMetadata(folderUri, options) {
    return this.asset2.getFolderMetadata(folderUri, options);
  }

  /**
   * @description
   * Create a folder under the given folder_uri.
   * @method
   * @param {object} parameters - input parameters, e.g. {parent_uri, name}
   * @param {object} options - parameters to append to the formData.
   * @returns {Promise} - A promise that resolves to asset JSON object now set with a asset_uri
   * @public
   */
  createFolder(parameters, options) {
    return this.asset2.createFolder(parameters, options);
  }

  /**
   * @description
   * Delete a folder matching the given folder_uri
   * @method
   * @param {object} options - the options to add to the DCAPI call, e.g. {uri_parameters: {...}}
   * @returns {Promise} - A promise that resolves to the deleted folderUri
   * @public
   */
  deleteFolder(folderUri) {
    return this.asset2.deleteFolder(folderUri);
  }

  /**
   * @description
   * Given an asset id, get an asset URI.
   * @method
   * @param {string} assetId - The asset id value (actually, an asset URN)
   * @returns {string} - an asset URI
   * @public
   */
  getAssetURI(assetId) {
    return this.asset2.getAssetURI(assetId);
  }

  /**
   * @description
   * Given a folder id, get a folder URI.
   * @method
   * @param {string} folderId - The folder id value (actually, an folder URN)
   * @returns {string} - a folder URI
   * @public
   */
  getFolderURI(folderId) {
    return this.asset2.getFolderURI(folderId);
  }

  /**
   * @description
   * Move either a file or folder
   * @method
   * @param {string} objectUri - A file or a folder to be moved.
   * @param {object} targetParent - The parent object where the object is to be moved to. Contains a uri and a name
   * @param {Object} [options] - Options such as on_dup_name(How to handle duplicate name) can be provided.
   * @returns {Promise} - A promise that resolves to
   * @public
   */
  moveObject(objectUri, targetParent, options) {
    return this.asset2.moveObject(objectUri, targetParent, options);
  }

  /**
   * @description
   * Update the starred and favorite fields of metadata for a folder
   * @method
   * @param {string} folderUri - The uri of the folder whose metadata is to be updated.
   * @param {string} fields - Single field name or comma separated names of metadata fields to update,
   * Eg: fields = "starred" or fields = "starred, favorite"
   * @param {Object} [options] - Optional fields that can help in identifying the folder, eg {name: "", uri: ""}
   * The options also contains the key, value pairs for the fields if any of the field is being updated
   * options: {starred: true, favorite: false}
   * @public
   */
  putFolderMetadata(folderUri, fields, options) {
    return this.asset2.putFolderMetadata(folderUri, fields, options);
  }

  /**
   * @description
   * Validate that a filename is operating system safe and DOM safe.
   * This logic is based on Blue Heron's file_utils.validate_name_legal()
   * File names will be tested with whitespace stripped.
   * @method
   * @param {string} filename - The filename to be validated with the extension.
   * @example
   * validateFilename("SampleFilename.pdf") returns true
   * Any of the following conditions will return false:
   *    1. Length < 1 or > 255
   *    2. Contains any of <>:\/"?|*
   *    3. Contains unicode control characters U+0000 - U+001F
   *    4. Begins or ends with '.' (including "." and "..")
   *    5. Is equal to one of "CON", "PRN", "AUX", "NUL", "COM{n}", "LPT{n}"
   */
  validateFilename(filename) {
    return this.asset2.validateFilename(filename);
  }

  /**
   * @description
   * Method to download multiple files or a single folder as a zip file
   * @method
   * @param {string} [folderUri] - The folder to create zip file from.
   * @param {string} zipFileName - Optional name to give to the zip file.
   * Default name is the name of the folder provided.
   * @param {number} timeZoneOffsetMinutes - Optional time zone offset of client's local time from GMT
   * Allows client to get zip file with client local modified times.
   * The default timezone offset is GMT i.e. 0, min. is -1440 and max. is 1440.
   * @param {boolean} makeTicket - Optional parameter to generate a download ticket valid for 1 minute
   * included in the uri. Defaults to false. Usually true for URLs used from browser.
   * @returns {string} - The download uri for the zip file created.
   */
  getZipDownloadUriFromFolder(folderUri, zipFileName, timeZoneOffsetMinutes, makeTicket) {
    return this.asset2.getZipDownloadUriFromFolder(folderUri, zipFileName, timeZoneOffsetMinutes, makeTicket);
  }

  /**
   * Method to download a zip file given a zip_download_uri
   * @emthod
   * @param {string} zipDownloadUri - The uri received from getZipDownloadUri operation
   * @returns {object} - The zip file.
  */
  downloadZip(zipDownloadUri) {
    return this.asset2.downloadZip(zipDownloadUri);
  }

  /**
   * @description
   * Delete selected metadata fields associated with an asset.
   * @method
   * @param {Asset} asset - Input asset whose metadata is to be deleted
   * @returns {Promise} - A promise that resolves to JSON object with meta-data from DCAPI
   * @public
   */
  deleteAssetMetadata(asset, options) {
    return this.asset2.deleteAssetMetadata(asset, options);
  }
}

// This allows for providers.x().then() to be called before providers.x(config).
AssetAPI.getInstance = providerUtil.getSingletonFunction(AssetAPI);
export default AssetAPI;
